// REF-6708 Phase A3 (U-1) — what Session Replay may record of DOM ATTRIBUTES, on a page whose URL is safe (the URL
// itself is replayGate's job). Public `replayIntegration` options only (@sentry/react 10.43); no SDK internals.
//
// How 10.43 treats an attribute (rrweb `transformAttribute`, read for this slice):
//   - `href`, `src`, `srcset`, `xlink:href`, `background`, `<object data>` and inline `style` are made ABSOLUTE
//     and returned BEFORE the mask callback runs, so `maskAttributes` can never mask them. The only public
//     option that keeps them out is `block`: a blocked element is recorded as an empty box with its `class`
//     and size only, without its other attributes or its children. `blockAllMedia` (on by default, pinned
//     here) already blocks `img`, `svg`, `video`, `picture`, `audio`, `object`, `embed`, `map` and icon links.
//   - every OTHER attribute goes through the mask callback, which masks the names in `maskAttributes` — but
//     only while `maskAllText` is on (pinned here). A masked value keeps its length (`*` per character).
//   - input values are masked by `maskAllInputs` (pinned here).
// So: elements that can CARRY a URL in an absolutised attribute are blocked unless the value is a plain in-app
// path; every other attribute that can carry a URL or user text is masked by name.
import { API_SECRET_PREFIXES, APP_SECRET_RULES } from "utils/privacy/telemetryUrl";

/** The literal path before the first secret segment of each `PATHS` secret route (`/reset-password/`, …). */
const secretPathPrefixes = (): string[] =>
  APP_SECRET_RULES.map((rule) => {
    const literals: string[] = [];
    for (const segment of rule) {
      if (!segment.literal) break;
      literals.push(segment.literal);
    }

    return `/${literals.join("/")}/`;
  });

// An `href` that can carry a secret wherever it appears: a query, a fragment, a percent-escape (CSS matches the RAW
// value, so `/%72eset-password/<t>` would slip past the prefix rule) or a secret-route prefix (case-insensitive).
const SECRET_HREF_CONDITIONS = [
  '[href*="?"]',
  '[href*="#"]',
  '[href*="%"]',
  ...[...secretPathPrefixes(), ...API_SECRET_PREFIXES.map((prefix) => `${prefix}/`)].map((prefix) => `[href*="${prefix}" i]`),
];
// A link (`a` / `area`) is recorded only when its `href` is also a plain in-app path: root-relative, not `//host`
// or `/\host`. So an absolute URL (a signed download, a `mailto:`), a `blob:` / `data:` URL or a relative path
// blocks it too.
const NON_LOCAL_HREF_CONDITIONS = [':not([href^="/"])', '[href^="//"]', '[href^="/\\\\"]'];
// The Google Fonts stylesheets in `public/index.html` carry a (static) query; playback needs them.
const FONT_STYLESHEET = ':not([rel~="stylesheet" i][href^="https://fonts.googleapis.com/"])';

/** CSS selectors Replay blocks (recorded as an empty box: class and size only). */
export const REPLAY_BLOCK_SELECTORS: readonly string[] = [
  ...[...SECRET_HREF_CONDITIONS, ...NON_LOCAL_HREF_CONDITIONS].flatMap((condition) => [`a[href]${condition}`, `area[href]${condition}`]),
  // A head `<link>` (a page's Helmet canonical can outlive the gate's resume by a frame — T-A3-1): canonicals
  // always, any other link only on a secret condition, so absolute stylesheet / font links stay for playback.
  'link[rel~="canonical" i]',
  ...SECRET_HREF_CONDITIONS.map((condition) => `link[href]${condition}${FONT_STYLESHEET}`),
  // `maskAllInputs` does not cover a hidden input, and its `value` bypasses `maskAttributes` (R-1).
  'input[type="hidden" i]',
  // Any other element with an absolutised URL attribute (frames, image inputs, posters, …) — media is already
  // blocked by `blockAllMedia`.
  "iframe",
  "frame",
  "[src]",
  "[srcset]",
  "[poster]",
  "[background]",
  '[style*="url(" i]',
];

/** Attributes Replay masks by name (`*` per character). The SDK's defaults first, then ours. */
export const REPLAY_MASKED_ATTRIBUTES: readonly string[] = [
  "title",
  "placeholder",
  "aria-label",
  // URL- or user-text-bearing standard attributes that are NOT absolutised (so masking reaches them).
  "alt",
  "value",
  "label",
  "action",
  "formaction",
  "ping",
  "cite",
  "download",
  "aria-description",
  "aria-valuetext",
  "aria-placeholder",
  "aria-roledescription",
  // A `<meta name>`'s value (`og:` / `twitter:` metas are already dropped by the SDK's `slimDOMOptions`).
  "content",
  // App `data-*` attributes that hold a URL (see REPLAY_SAFE_DATA_ATTRIBUTES for the reviewed rest).
  "data-asset-url",
  "data-current-image",
  "data-crop-url",
  // A user-given annotation name in the SVG editor's markup (inside an `<svg>`, already blocked by
  // `blockAllMedia` — masked as well, in case that element is ever unblocked).
  "data-name",
  // A layer row's id in the SVG editor's Layers panel (LayerRow): the element's id, which an imported SVG carries
  // as its author wrote it (Illustrator exports layer names as ids).
  "data-row-id",
  // Studio row / product keys: minted with uuidv4 on add, but a reopened draft restores them from the server's
  // `IPhotoshootDraftLooks.mainProducts` / `combinationRows` (typed `unknown[]`, cast unvalidated), so their
  // content is not provably a generated id.
  "data-look-id",
  "data-look-card",
  "data-ghost-card",
  // Design Canvas agent (0adaa13080): a recipe chip's picture URL (`chip.image` in RecipeStage; its other uses are
  // "true" / "false", but classification is by name), the in-app route a resource chip opens (ids and paths), and
  // the person an assistant works for — `owner` is that person's display NAME (PresenceRoot matches `p.name === a.owner`).
  "data-image",
  "data-route",
  "data-owner",
  // H&M supplier list (REF-7272): a filter option's value — a supplier-list dimension value from the API.
  "data-value",
];

/**
 * App `data-*` attributes with a DYNAMIC value that are safe to record as written: ids, keys, indices, enum
 * flags. Every dynamic `data-*` in `src/` is either here or in REPLAY_MASKED_ATTRIBUTES — the inventory test
 * (`replayPrivacy.test.ts`) fails on an unlisted one, because CSS / `maskAttributes` cannot match a NAME pattern.
 */
export const REPLAY_SAFE_DATA_ATTRIBUTES: readonly string[] = [
  "data-testid",
  "data-page-index",
  "data-slot-key",
  "data-thread-id",
  "data-chatbot-open",
  "data-action-key",
  "data-version-id",
  "data-trend-id",
  "data-tab",
  "data-tab-id",
  "data-side",
  "data-pagefind-filter",
  "data-message-id",
  "data-docs-ready",
  "data-chip-key",
  "data-callout",
  "data-asset-type",
  "data-theme",
  // Studio variant-cap meter: the literal "true" | "false" (VariantLimitMeter, `over ? "true" : "false"`).
  "data-over",
  // Studio look-row slot flag: the literal enum back | fit | main | combination (CombinationProductRow).
  "data-highlight",
  // The SVG editor's own markers (layer / handle names, node ids, flags), inside its `<svg>`.
  "data-layer",
  "data-lockable",
  "data-handle",
  "data-node",
  "data-arrow",
  // Design Canvas agent page, canvas room presence, comments and activity (0adaa13080), plus main's later surfaces
  // (reviewed for the 2026-10-31 main sync). Every value is one of:
  //   booleans written as "true" / "false" / "" or left out;
  "data-active",
  "data-add-to-chat-shown",
  "data-agent",
  "data-answered",
  "data-approving",
  "data-boxed",
  "data-collapsed",
  "data-compact",
  "data-confirm",
  "data-deleted",
  "data-disabled",
  "data-draft",
  "data-dragging",
  "data-editing",
  "data-embedded",
  "data-estimated",
  "data-expanded",
  "data-focus",
  "data-folded",
  "data-followed",
  "data-generating",
  "data-hidden",
  "data-idle",
  "data-in-place",
  "data-locked",
  "data-multi",
  "data-row-nested",
  "data-next",
  "data-on",
  "data-open",
  "data-picked",
  "data-queued",
  "data-resolved",
  "data-selected",
  "data-self",
  "data-sending",
  "data-stacked",
  "data-stopping",
  "data-streaming",
  "data-undone",
  //   enums from the code (states, kinds, phases, modes, placements, catalog task / tool / navigate-target names);
  "data-activity",
  "data-chip",
  "data-decision",
  "data-fade",
  "data-job-state",
  "data-kind",
  "data-lane",
  "data-list-mode",
  "data-media-state",
  "data-mode",
  "data-notice",
  "data-outcome",
  "data-phase",
  "data-placement",
  "data-plan-action-state",
  "data-preview",
  "data-reason",
  "data-reveal",
  "data-role",
  "data-roles",
  "data-scroll-mode",
  "data-source",
  "data-state",
  "data-step-state",
  "data-target",
  "data-task",
  "data-tool-name",
  "data-turn-role",
  "data-turn-state",
  "data-type",
  "data-parts",
  "data-colour",
  //   numbers (counts, credits, positions, sizes, percentages);
  "data-cap",
  "data-ceiling",
  "data-chain",
  "data-charged",
  "data-columns",
  "data-count",
  "data-cover-height",
  "data-credits",
  "data-hero-tile",
  "data-jobs",
  "data-max",
  "data-percent",
  "data-steps",
  "data-tokens",
  "data-version",
  //   opaque ids and handles (uuids, job / run / plan / turn ids, record handles such as `resource://file/9`).
  "data-card-id",
  "data-chain-id",
  "data-collection-id",
  "data-dock-exit",
  "data-from-step",
  "data-input-id",
  "data-item-id",
  "data-job-id",
  "data-kit-section",
  "data-node-id",
  "data-option-id",
  "data-plan-id",
  "data-pointer",
  "data-question-id",
  "data-ref",
  "data-run-id",
  "data-step",
  "data-step-id",
  "data-tone",
  "data-tool-call-id",
  "data-turn-id",
  "data-unit",
];

/** The privacy options `instrument.ts` gives `replayIntegration` (pinned: changing a default here is a review). */
export const REPLAY_PRIVACY_OPTIONS = {
  maskAllText: true,
  maskAllInputs: true,
  blockAllMedia: true,
  block: [...REPLAY_BLOCK_SELECTORS],
  maskAttributes: [...REPLAY_MASKED_ATTRIBUTES],
};
